import React from 'react';
import _ from 'underscore';

import {ItemForArchive} from 'archive/ArchiveConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import FolderSelector from 'file-browser/FolderSelector';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import APIUtil from 'util/APIUtil';
import {GenericEvent} from 'util/Types';

import {ArchiveModalItem, ArchiveOptionsForModal} from './ArchiveModal';

interface ItemForArchiveWithFolderInfo extends ItemForArchive {
  projectFolder?: StringIDFolder | NumberIDFolder | null;
  folder?: StringIDFolder | NumberIDFolder | null;
  projectFolderId?: string | number | null;
}

export interface ArchiveRegisteredBioentitiesOptionProps {
  shouldRemoveShortcuts: boolean | null;
  shouldUnregisterBioentities: boolean | null;
  unregisteredBioentitiesFolderId: string | null;
  itemsForArchive: Array<ItemForArchiveWithFolderInfo>;
  onChange: (options: Partial<ArchiveOptionsForModal>) => void;
}

class ArchiveRegisteredBioentitiesOption extends React.Component<ArchiveRegisteredBioentitiesOptionProps> {
  static isEnabled(items: Array<ItemForArchiveWithFolderInfo>): boolean {
    return items.some(({isRegistered}) => isRegistered);
  }

  static getValidationError(options: ArchiveRegisteredBioentitiesOptionProps): string | null {
    if (options.shouldUnregisterBioentities == null) {
      return 'Please select a registry action.';
    }
    if (options.shouldUnregisterBioentities && options.unregisteredBioentitiesFolderId == null) {
      return 'Please select a folder to move unregistered entities into.';
    }
    return null;
  }

  static getDefaultState(items: Array<ArchiveModalItem>): Omit<
    ArchiveRegisteredBioentitiesOptionProps,
    'itemsForArchive' | 'onChange'
  > & {
    parentFolderIds: Array<string | number>;
  } {
    const projectFolderIds = _.compact(
      items.map((item) => (item.projectFolder ? item.projectFolder.api_identifier : null))
    );
    // if all items are in the same folder, display it as the default folder selection
    const initialUnregisteredBioentitiesFolderId =
      new Set(projectFolderIds).size === 1 ? projectFolderIds[0] : null;
    const parentFolderIds: Array<string | number> = [];
    for (const item of items) {
      // @ts-expect-error ArchiveModal passes ArchiveModalItem, which does not have folder
      if (item.folder) {
        // @ts-expect-error ArchiveModal passes ArchiveModalItem, which does not have folder
        parentFolderIds.push(item.folder.id);
      }
      // @ts-expect-error ArchiveModal passes ArchiveModalItem, which does not have projectFolderId
      if (item.projectFolderId) {
        parentFolderIds.push(item.projectFolder!.api_identifier);
      }
    }
    return {
      shouldRemoveShortcuts: null,
      shouldUnregisterBioentities: null,
      unregisteredBioentitiesFolderId: initialUnregisteredBioentitiesFolderId,
      parentFolderIds,
    };
  }

  static getActions(itemsForArchive: Array<ItemForArchiveWithFolderInfo>): Array<{
    text: string;
    value: Pick<
      ArchiveRegisteredBioentitiesOptionProps,
      'shouldUnregisterBioentities' | 'shouldRemoveShortcuts'
    >;
  }> {
    return _.compact([
      {
        text: 'Unregister registered entities',
        value: {shouldUnregisterBioentities: true, shouldRemoveShortcuts: false},
      },
      {
        text: 'Keep registered entities in registry',
        value: {shouldUnregisterBioentities: false, shouldRemoveShortcuts: false},
      },
      itemsForArchive.some(({isShortcut}) => isShortcut) &&
        itemsForArchive.some(({id}) => APIUtil.isFolder(id)) && {
          text: 'Remove entities from project',
          value: {shouldUnregisterBioentities: false, shouldRemoveShortcuts: true},
        },
    ]);
  }

  _handleChangeOptions = ({
    shouldUnregisterBioentities,
    shouldRemoveShortcuts,
  }: {
    shouldUnregisterBioentities: boolean;
    shouldRemoveShortcuts: boolean;
  }): void => {
    this.props.onChange({shouldUnregisterBioentities, shouldRemoveShortcuts});
  };

  _handleChangeFolder = ({target: {value}}: GenericEvent): void => {
    // If value is falsey (e.g. empty string), fallback to null (see BNCH-10194)
    this.props.onChange({unregisteredBioentitiesFolderId: value || null});
  };

  override render(): JSX.Element {
    return (
      <Row className="u-spacing-top">
        <Col md={12}>
          <FieldLabel label="Registry Action" />
          <SelectDropdown
            displayName={
              this.props.shouldUnregisterBioentities == null
                ? 'Choose action...'
                : ArchiveRegisteredBioentitiesOption.getActions(this.props.itemsForArchive).find(
                    (action) =>
                      action.value.shouldUnregisterBioentities ===
                        Boolean(this.props.shouldUnregisterBioentities) &&
                      action.value.shouldRemoveShortcuts === Boolean(this.props.shouldRemoveShortcuts)
                  )!.text
            }
            menu={
              <Menu
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.FULL}
                value={{
                  shouldUnregisterBioentities: this.props.shouldUnregisterBioentities,
                  shouldRemoveShortcuts: this.props.shouldRemoveShortcuts,
                }}
              >
                {ArchiveRegisteredBioentitiesOption.getActions(this.props.itemsForArchive).map(
                  ({text, value}) => (
                    <Option key={text} onSelect={this._handleChangeOptions} value={value}>
                      {text}
                    </Option>
                  )
                )}
              </Menu>
            }
          />
          {this.props.shouldUnregisterBioentities && (
            <FolderSelector
              className="u-small-spacing-top"
              level="append"
              onChange={this._handleChangeFolder}
              showInventory={true}
              value={this.props.unregisteredBioentitiesFolderId}
              unsetOnFetchFail={false}
            />
          )}
        </Col>
      </Row>
    );
  }
}

export default ArchiveRegisteredBioentitiesOption;
